import { Dispatch, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from 'components';
import { PsCheckboxTuner } from 'stencilSetup';
import { Link } from 'theSource/components';
import {
  UnumApplicationRequestDto,
  UnumApplicationSurveyResponsesDto,
} from '../types';
import { UnumApplicantDto, UnumDocumentDto } from 'enrollment/queries';
import { isHiddenDocument } from '../utils';

interface UnumApplicationDocumentsProps {
  documents: UnumDocumentDto[];
  responses: UnumApplicationRequestDto;
  setResponses: Dispatch<SetStateAction<UnumApplicationRequestDto>>;
  surveyResponses: UnumApplicationSurveyResponsesDto;
  surveyQuestions: UnumApplicantDto[];
}

export const UnumApplicationDocuments = ({
  documents,
  responses,
  setResponses,
  surveyResponses,
  surveyQuestions,
}: UnumApplicationDocumentsProps) => {
  const { t } = useTranslation('enrollment.plugins');

  const handleClick = (e: any) => {
    if (e.target.tagName !== 'INPUT') {
      return;
    }

    const id = e.target.id;
    const idLength = id.split('-').length;
    const formattedId = id.split('-')[idLength - 1];
    const newResponses = { ...responses };

    if (e.target.checked) {
      newResponses.documents[formattedId] = 'Y';
    } else {
      newResponses.documents[formattedId] = 'N';
    }

    setResponses(newResponses);
  };

  // remove previously answered docs from array if now hidden
  useEffect(() => {
    if (surveyResponses) {
      const newResponses = { ...responses };
      documents.forEach((document, index) => {
        if (isHiddenDocument(surveyResponses, surveyQuestions, document)) {
          delete newResponses.documents[index];
        }
      });
      setResponses(newResponses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyResponses]);

  return (
    <Card
      additionalClasses="card"
      dataTestId="enrollment-unum-documents-card"
      showBorder
    >
      <h4>
        {t('unum.Application.documentsTitle')}
        <span>
          <span aria-hidden="true" role="presentation" className="red">
            *
          </span>
          <span className="sr-only">{t('common:required')}</span>
        </span>
      </h4>
      <div>
        <table className="table" data-testid="unum-documents-table">
          <thead>
            <tr>
              <th className="body semiBold px-0 pb-0">
                {t('unum.Application.documentTableTitle')}
              </th>
              <th className="body semiBold px-0 pb-0">
                {t('unum.Application.documentTableConfirm')}
              </th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document, index) => {
              if (
                isHiddenDocument(surveyResponses, surveyQuestions, document)
              ) {
                return null;
              }
              return (
                <tr key={index}>
                  <td className="p-0 pt-3">
                    <Link
                      sx={{ fontWeight: 600 }}
                      value={document.Description}
                      underline={true}
                      testId={`unum-documents-link-${index}`}
                      path={document.Path}
                      target="_blank"
                    />
                  </td>
                  <td className="p-0">
                    <PsCheckboxTuner
                      data-testid={`unum-documents-checkbox-${index}`}
                      inputId={`unum-documents-checkbox-${index}`}
                      name="unum-documents-checkbox"
                      label={document.ActionText}
                      checked={document.Confirmed === 'Y'}
                      onClick={handleClick}
                      value={document.Action}
                      required={true}
                      requiredWord={t('required')}
                      otherData={[
                        {
                          attribute: 'role',
                          value: 'checkbox',
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
